import * as React from 'react';
import { ITextFieldProps, TextField } from '@fluentui/react';
import { KeyCodes } from '../../util/Keyboard';

export interface TrackingTextFieldProps extends ITextFieldProps {
  onFinishedUpdate?: (value: string) => void;
}

export interface TrackingTextFieldState {
  focused: boolean;
  lastSavedValue?: string;
}

/*
 * TrackingTextField
 *
 * This class adds utility to TextField by abstracting the onFinishedUpdate method,
 * which fires when the user either moves focus from the current field or presses the enter key.
 * It only fires when the value on completion is different from the previous value.
 * This also useful for scenarios where you don't want all intermediate events between values, such as onChange
 * would provide, or you don't want to write the code to extract values from onBlur in multiple places.
 */
export class TrackingTextField extends React.Component<TrackingTextFieldProps, TrackingTextFieldState> {
  private pendingValue: string;

  /*
   * Lifecycle
   */
  public constructor(props: TrackingTextFieldProps) {
    super(props);

    this.pendingValue = this.props.defaultValue || '';
    this.state = {
      focused: false,
      lastSavedValue: this.props.defaultValue,
    };
  }

  public render(): JSX.Element {
    return <TextField {...this.props} onBlur={this.onBlur} onChange={this.onChange} onFocus={this.onFocus} onKeyDown={this.onKeyDown} />;
  }

  private valueHasChanged(): boolean {
    return this.pendingValue !== this.state.lastSavedValue;
  }

  /*
   * Lambdas
   */
  private onBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const currentValue = this.pendingValue;
    if (this.props.onFinishedUpdate && this.valueHasChanged()) {
      this.props.onFinishedUpdate(currentValue);
    }

    this.setState({
      focused: false,
      lastSavedValue: currentValue,
    });

    if (this.props.onBlur) {
      this.props.onBlur(e);
    }
  };

  /*
   * ref is not reliable for text field so we use onChange to track the new value
   */
  private onChange = (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string): void => {
    this.pendingValue = newValue || '';
    if (this.props.onChange) {
      this.props.onChange(e);
    }
  };

  private onFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    this.setState({ focused: true });

    if (this.props.onFocus) {
      this.props.onFocus(e);
    }
  };

  private onKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    if (e.keyCode === KeyCodes.enter) {
      e.preventDefault();
      e.currentTarget.blur();
    }
  };
}
